import { create } from 'zustand';
import { courseService } from '../services/courseService';
import { certificateService } from '../services/certificateService';
import { CourseAssignment, Certificate, Announcement } from '../types';

interface EmployeeState {
  userCourses: Record<string, CourseAssignment>;
  certificates: Certificate[];
  announcements: Announcement[];
  isLoading: boolean;
  error: string | null;
  fetchEmployeeData: () => Promise<void>;
  initialize: () => Promise<void>;
  updateCourseInStore: (updatedCourse: CourseAssignment) => void;
  clearEmployeeData: () => void;
}

export const useEmployeeStore = create<EmployeeState>((set) => ({
  // Initial state - no localStorage persistence
  userCourses: {},
  certificates: [],
  announcements: [],
  isLoading: false,
  error: null,

  clearEmployeeData: () => {
    set({
      userCourses: {},
      certificates: [],
      announcements: [],
      isLoading: false,
      error: null
    });
  },

  fetchEmployeeData: async () => {
    try {
      set({ isLoading: true, error: null });
      
      const [courses, certificates] = await Promise.all([
        courseService.getUserCourses(),
        certificateService.getAllCertificatesForEmployee()
      ]);

      // Convert courses array to object with course IDs as keys
      const coursesObject = Array.isArray(courses) 
        ? courses.reduce((acc, course) => {
            acc[course.id] = course;
            return acc;
          }, {} as Record<string, CourseAssignment>)
        : courses;

      // Update state - no localStorage persistence
      set({
        userCourses: coursesObject,
        certificates,
        isLoading: false
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false 
      });
    }
  },

  initialize: async () => {
    const store = useEmployeeStore.getState();
    // Always fetch fresh data - no localStorage check
    await store.fetchEmployeeData();
  },

  updateCourseInStore: (updatedCourse) => set((state) => ({
    userCourses: {
      ...state.userCourses,
      [updatedCourse.id]: updatedCourse
    }
  }))
}));